import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Switch,
  TextField,
  IconButton,
  Button,
  makeStyles,
  CircularProgress,
  Typography,
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import SaveIcon from "@material-ui/icons/Save";
import CancelIcon from "@material-ui/icons/Cancel";
import axios from "axios";
import { appUrl } from "../../../../../utils/config";

/*
  BCM-881 — the Inventory Status tab in Sponsor Network Configuration.

  It holds the inventory statuses available across the network. These are the
  statuses offered in Status Mapping in the CMO Inventory Mapping, where each
  CMO's own status or column names are mapped onto them.

  Each row carries a Selection toggle, the status name, a system generated
  Inventory Status ID and a delete action; Add New Inventory Status adds a row,
  and Save Selection / Reset Selection commit or discard.

  Endpoints: fetchInventoryStatuses, addInventoryStatuses (name only; the
  Inventory Status ID is generated by Corda as MAX + 1 and the middleware saves
  the status switched on), updateInventoryStatuses (Selection toggle and
  delete, by statusID; a saved name cannot be changed).
*/
const BASE = () =>
  `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}`;

// A refusal can come back as HTTP 200 with status false; only an explicit
// false counts as one.
const rejectionOf = (response, fallback) => {
  const data = response?.data;
  if (!data || data.status !== false) return "";
  return data.errorMessage || data.errorDescription || data.message || fallback;
};

/*
  The middleware refuses most changes with HTTP 400 / 500 and a body of
  { status: false, message, errors?: [..], error?: { errorMessage } }, where
  errors lists the validation failures and error carries Corda's own reason
  (for example "Inventory status 7 does not exist"). axios throws on those
  codes, so the reason is read off err.response.data here.
*/
const reasonOf = (payload, fallback) => {
  if (!payload || typeof payload !== "object") return fallback;
  const parts = [];
  if (Array.isArray(payload.errors) && payload.errors.length) {
    parts.push(payload.errors.join(" "));
  } else if (payload.message && !/^(Error from Corda|Validation failed)$/.test(payload.message)) {
    parts.push(payload.message);
  }
  const corda =
    payload.error && typeof payload.error === "object" ? payload.error.errorMessage : payload.error;
  if (typeof corda === "string" && corda) parts.push(corda);
  return parts.join(" ") || payload.errorMessage || payload.errorDescription || fallback;
};

// A Corda write can be slow; past this the tab stops waiting and shows what is saved.
const CHANGE_TIMEOUT_MS = 60000;

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    padding: theme.spacing(2),
  },
  table: {
    backgroundColor: "#fff",
    border: "1px solid #ddd",
    width: "640px",
  },
  headerCell: {
    fontWeight: 600,
    color: "#333",
    backgroundColor: "#f0f0f0",
    padding: "10px 16px",
    textAlign: "left",
  },
  bodyCell: {
    padding: "10px 16px",
    textAlign: "left",
    verticalAlign: "middle",
  },
  textField: {
    "& input": {
      padding: "6px 8px",
      height: "30px",
    },
  },
  addButton: {
    marginTop: theme.spacing(2),
    backgroundColor: "#fff",
    color: "#000",
    borderRadius: "20px",
    padding: "6px 16px",
    textTransform: "none",
    fontWeight: 600,
    border: "1px solid #000",
    display: "flex",
    alignItems: "center",
    "&:hover": { backgroundColor: "#f5f5f5" },
  },
  addIconCircle: {
    width: "24px",
    height: "24px",
    borderRadius: "50%",
    border: "2px solid #000",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginRight: theme.spacing(1),
    fontSize: "16px",
    fontWeight: 600,
  },
  deleteIcon: { color: theme.palette.error.main },
  switchBase: {
    "&.Mui-checked": { color: "#007bff" },
    "&.Mui-checked + .MuiSwitch-track": { backgroundColor: "#007bff" },
  },
  iconBtn: { padding: "4px" },
  inlineActions: { display: "flex", flexDirection: "column", marginLeft: 8 },
  saveButton: {
    backgroundColor: "#ec6535",
    color: "#fff",
    borderRadius: "20px",
    padding: "8px 24px",
    textTransform: "none",
    fontWeight: 600,
    whiteSpace: "nowrap",
    "&:hover": { backgroundColor: "#d4552a" },
  },
  resetChangesButton: {
    border: "1px solid #000000",
    color: "#000000",
    fontWeight: 600,
    borderRadius: "20px",
    padding: "6px 16px",
    textTransform: "none",
    whiteSpace: "nowrap",
  },
}));

const InventoryStatusTab = (props) => {
  const classes = useStyles();
  const { featuresAccess, sponsorId, onStatusesFetched } = props;
  const canEdit = featuresAccess?.editSponsorNetworkDetails ?? false;

  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isUpdated, setIsUpdated] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  // The change in flight -- "add-<row>", "delete-<statusID>" or "save" -- so its
  // control shows a spinner and no second change starts meanwhile.
  const [busy, setBusy] = useState(null);

  /*
    pending: statusID -> { selected, markForDelete } for the changes a user has
    made but not saved. Saving one typed row re-reads the list, and without this
    those changes would be silently reverted to what the server holds.
  */
  const fetchInventoryStatuses = async (pending = null) => {
    setLoading(true);
    try {
      // cmoConfigBool false here: this tab maintains every status, active or
      // not. The Status Mapping dropdown in Add/Edit CMO passes true, which
      // narrows the list to the activated ones.
      const response = await axios.post(`${BASE()}/fetchInventoryStatuses`, {
        sponsorId,
        cmoConfigBool: false,
      });
      const rejected = rejectionOf(response, "Failed to load Inventory Statuses.");
      if (rejected) {
        setErrorMessage(rejected);
        setStatuses([]);
        if (onStatusesFetched) onStatusesFetched([]);
        return;
      }
      const list =
        response?.data?.data?.inventoryStatuses ||
        response?.data?.inventoryStatuses ||
        [];
      /*
        The id used to fall back to the row's position (`?? index + 1`) when
        the response carried no statusID. That number is what a toggle or a
        removal is keyed on, so a row that fell back would have switched off or
        deleted whichever status happened to hold that ID. Nothing is invented
        now: a row with no statusID is shown without one and cannot be changed.
      */
      const formatted = list.map((item) => {
        const id = item.statusID ?? null;
        const saved = !!item.activated;
        const held = pending && id !== null ? pending.get(id) : undefined;
        if (!held) return { id, name: item.statusName || "", selected: saved, isNew: false };
        return {
          id,
          name: item.statusName || "",
          selected: held.selected,
          isNew: false,
          isUpdated: held.selected !== saved,
          markForDelete: held.markForDelete,
        };
      });
      setStatuses(formatted);
      if (onStatusesFetched) onStatusesFetched(formatted);
    } catch (err) {
      console.error("fetchInventoryStatuses:", err);
      setErrorMessage("Failed to load Inventory Statuses. Please try again.");
      setStatuses([]);
      if (onStatusesFetched) onStatusesFetched([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInventoryStatuses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleToggle = (index) => {
    const updated = [...statuses];
    updated[index].selected = !updated[index].selected;
    updated[index].isUpdated = true;
    setStatuses(updated);
    setIsUpdated(true);
  };

  const handleNameChange = (index, value) => {
    const updated = [...statuses];
    updated[index].name = value;
    setStatuses(updated);
  };

  // The Inventory Status ID is generated by the system on save (FSD), so a new
  // row carries none until the list is re-read after the add. It is saved
  // switched on, so it is shown that way.
  const handleAddRow = () => {
    setStatuses([...statuses, { id: null, name: "", selected: true, isNew: true }]);
    setIsUpdated(true);
  };

  // Whether anything is waiting to be saved: a typed row, a flipped toggle or a
  // row marked for removal. Save Selection and Reset Selection follow it.
  const hasPendingChanges = (list) =>
    list.some((item) => item.isNew || item.isUpdated || item.markForDelete);

  const handleCancelRow = (index) => {
    const remaining = statuses.filter((_, i) => i !== index);
    setStatuses(remaining);
    setErrorMessage("");
    setIsUpdated(hasPendingChanges(remaining));
  };

  /*
    Runs one change against the middleware and re-reads the list afterwards,
    so the table always shows what is saved. Returns "ok", "timeout" (no answer
    in time -- the change may still have been saved, so the list is re-read and
    the user told to check) or false (refused; the reason is shown).
  */
  const runChange = async (
    key,
    operation,
    body,
    successText,
    failureText,
    // Save Selection sends an add and an update one after the other, and only
    // re-reads once both are through: a re-read in between would replace the
    // rows still waiting to be sent.
    rereadAfter = true
  ) => {
    if (busy && busy !== key) return false;
    setBusy(key);
    setErrorMessage("");
    setSuccessMessage("");
    // As on the other Sponsor Network tabs: a save shows the table's own loader
    // until the list is re-read; add and delete show a spinner in their row.
    const tableLoader = key === "save";
    if (tableLoader) setLoading(true);
    let reread = false;
    try {
      const response = await axios.post(`${BASE()}/${operation}`, body, {
        timeout: CHANGE_TIMEOUT_MS,
      });
      const rejected = rejectionOf(response, failureText);
      if (rejected) {
        setErrorMessage(rejected);
        return false;
      }
      if (successText) setSuccessMessage(successText);
      if (rereadAfter) {
        reread = true;
        await fetchInventoryStatuses();
      }
      return "ok";
    } catch (err) {
      console.error(`${operation}:`, err);
      if (err?.code === "ECONNABORTED") {
        setErrorMessage(
          "No response from the server yet. The list has been refreshed - check whether the change was saved before trying again."
        );
        reread = true;
        await fetchInventoryStatuses();
        return "timeout";
      }
      setErrorMessage(reasonOf(err?.response?.data, failureText));
      // 504: the middleware gave up waiting on Corda, which may still save the
      // change, so show what is saved now.
      if (err?.response?.status === 504) {
        reread = true;
        await fetchInventoryStatuses();
        return "timeout";
      }
      return false;
    } finally {
      // fetchInventoryStatuses clears the loader itself when it re-reads.
      if (tableLoader && !reread) setLoading(false);
      setBusy(null);
    }
  };

  // The name a row would be saved under, trimmed.
  const typedName = (status) => (status?.name || "").trim();

  /*
    Whether the typed name clashes with another row on screen, compared the way
    the middleware compares it -- case-insensitively, against rows still in play
    (a row marked for removal is not a clash). The save icon stays disabled
    while it does, so the refusal is shown before the request rather than after.
  */
  const isDuplicateName = (index) => {
    const name = typedName(statuses[index]).toLowerCase();
    if (!name) return false;
    return statuses.some(
      (item, i) => i !== index && !item.markForDelete && typedName(item).toLowerCase() === name
    );
  };

  /*
    A typed row is sent on its own save icon, the way every other Sponsor
    Network tab does it -- Material Type, UOM, Batch Status, Plant Type and PO
    Status all carry a save beneath the cancel and call their add endpoint from
    it. Save Selection then carries the flipped toggles and the removals.

    A delete still waits for Save Selection, because a removal is keyed on a
    statusID the row already has, so nothing is lost by holding it.
  */
  const handleSaveNewRow = async (index) => {
    const status = statuses[index];
    const name = typedName(status);
    if (!name || busy) return;

    /*
      Saving one row re-reads the list, which would otherwise throw away the
      toggles and removals still waiting for Save Selection. They are carried
      across the re-read instead, so the two ways of saving do not fight.
    */
    const pending = new Map(
      statuses
        .filter((item) => !item.isNew && item.id !== null && (item.isUpdated || item.markForDelete))
        .map((item) => [item.id, { selected: item.selected, markForDelete: !!item.markForDelete }])
    );

    // rereadAfter false: the re-read below carries the pending changes with it.
    const result = await runChange(
      `add-${index}`,
      "addInventoryStatuses",
      {
        sponsorId,
        inventoryStatuses: [
          { statusName: name, activated: !!status.selected, markForDelete: false },
        ],
      },
      `Inventory status "${name}" added.`,
      "Failed to add the inventory status.",
      false
    );
    if (!result) return;
    await fetchInventoryStatuses(pending);
    setIsUpdated(pending.size > 0);
  };

  // A new row is simply dropped; a saved one is marked, hidden, and removed
  // when Save Selection goes through.
  const handleDelete = (status) => {
    const updated = statuses
      .filter((item) => !(item === status && item.isNew))
      .map((item) => (item === status ? { ...item, markForDelete: true } : item));
    setStatuses(updated);
    setErrorMessage("");
    setIsUpdated(hasPendingChanges(updated));
  };

  /*
    Save Selection commits everything waiting: the typed rows through
    addInventoryStatuses, and the flipped toggles and the removals through
    updateInventoryStatuses. The add goes first so a refusal leaves the rest
    untouched, and the list is re-read once, after both.
  */
  const handleSaveChanges = async () => {
    const added = statuses.filter((status) => status.isNew && !status.markForDelete);
    const kept = statuses.filter((status) => !status.isNew && !status.markForDelete);
    // updateInventoryStatuses is keyed on statusID, so a row without one is
    // never sent. The controls for such a row are disabled, so this only
    // guards against it being reached another way.
    const removed = statuses.filter(
      (status) => !status.isNew && status.markForDelete && status.id !== null
    );
    const toggled = kept.filter((status) => status.isUpdated && status.id !== null);
    if (!added.length && !removed.length && !toggled.length) return;

    const names = added.map((status) => status.name?.trim());
    if (names.some((name) => !name)) {
      setSuccessMessage("");
      setErrorMessage("Type a name for every new inventory status, or remove the row.");
      return;
    }
    const lower = names.map((name) => name.toLowerCase());
    const clash =
      lower.find((name, i) => lower.indexOf(name) !== i) ||
      lower.find((name) =>
        kept.some((status) => status.name?.trim().toLowerCase() === name)
      );
    if (clash) {
      setSuccessMessage("");
      setErrorMessage(`Inventory status already exists: ${clash}.`);
      return;
    }
    // At least one status stays switched on, as before.
    const remaining = [...kept, ...added];
    if (remaining.length && remaining.every((status) => !status.selected)) {
      setSuccessMessage("");
      setErrorMessage("Please enable at least one in toggle mode.");
      return;
    }

    if (names.length) {
      const result = await runChange(
        "save",
        "addInventoryStatuses",
        {
          sponsorId,
          inventoryStatuses: added.map((status, index) => ({
            statusName: names[index],
            activated: !!status.selected,
            markForDelete: false,
          })),
        },
        "",
        "Failed to add the inventory status.",
        // Only re-read once the update below has gone too.
        !toggled.length && !removed.length
      );
      // Refused: the edits stay on screen to correct.
      if (!result) return;
    }

    if (toggled.length || removed.length) {
      const result = await runChange(
        "save",
        "updateInventoryStatuses",
        {
          sponsorId,
          inventoryStatuses: [
            ...toggled.map(({ id, selected }) => ({
              statusID: Number(id),
              activated: selected,
              markForDelete: false,
            })),
            ...removed.map(({ id, selected }) => ({
              statusID: Number(id),
              activated: selected,
              markForDelete: true,
            })),
          ],
        },
        "",
        "Failed to update Inventory Statuses."
      );
      if (!result) return;
    }

    setSuccessMessage("Selection saved.");
    setIsUpdated(false);
  };

  const handleResetChanges = () => {
    if (!isUpdated || busy) return;
    fetchInventoryStatuses();
    setIsUpdated(false);
    setErrorMessage("");
    setSuccessMessage("");
  };

  return (
    <div
      className={classes.container}
      style={{ display: "flex", flexDirection: "column", gap: "24px" }}
    >
      <div style={{ display: "flex", gap: "40px", alignItems: "flex-start" }}>
        <Table className={classes.table}>
          <TableHead>
            <TableRow>
              <TableCell className={classes.headerCell}>Selection</TableCell>
              <TableCell className={classes.headerCell}>Inventory Status</TableCell>
              <TableCell className={classes.headerCell} style={{ whiteSpace: "nowrap" }}>
                Inventory Status ID
              </TableCell>
              <TableCell className={classes.headerCell}>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "100px",
                    }}
                  >
                    <CircularProgress />
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              // A row marked for removal is kept in place, so the indices the
              // handlers use stay put, and simply not drawn until Save
              // Selection goes through or Reset Selection brings it back.
              statuses.map((item, index) => item.markForDelete ? null : (
                <TableRow key={item.id || `new-${index}`}>
                  <TableCell className={classes.bodyCell}>
                    <Switch
                      checked={item.selected}
                      onChange={() => handleToggle(index)}
                      classes={{ switchBase: classes.switchBase }}
                      // A new row carries its own toggle out on the add call, the
                      // way the other configuration tabs do. A saved row with no
                      // statusID cannot be keyed, so it is left alone rather than
                      // sent under a guessed ID.
                      disabled={!canEdit || (!item.isNew && item.id === null)}
                    />
                  </TableCell>
                  <TableCell className={classes.bodyCell}>
                    {item.isNew ? (
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <TextField
                          placeholder="Type Text Here"
                          value={item.name}
                          onChange={(e) => handleNameChange(index, e.target.value)}
                          onBlur={(e) => {
                            const { value } = e.target;
                            if (typeof value === "string" && value.trim() !== value) {
                              handleNameChange(index, value.trim());
                            }
                          }}
                          className={classes.textField}
                          variant="outlined"
                          inputProps={{
                            onBeforeInput: (e) => {
                              const allowed = /^[a-zA-Z0-9\s()/\-.]+$/;
                              if (e.data && !allowed.test(e.data)) e.preventDefault();
                            },
                          }}
                        />
                        {/* Cancel drops the row; save beneath it sends the row,
                            the same pair the other Sponsor Network tabs carry. */}
                        <div className={classes.inlineActions}>
                          <IconButton
                            onClick={() => handleCancelRow(index)}
                            className={classes.iconBtn}
                            disabled={!!busy}
                          >
                            <CancelIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            onClick={() => handleSaveNewRow(index)}
                            className={classes.iconBtn}
                            disabled={!canEdit || !!busy || !typedName(item) || isDuplicateName(index)}
                          >
                            <SaveIcon fontSize="small" />
                          </IconButton>
                        </div>
                      </div>
                    ) : (
                      item.name
                    )}
                  </TableCell>
                  {/* Shown two digits wide, as the design has it: 01, 02, 03. */}
                  <TableCell className={classes.bodyCell}>
                    {item.isNew ? (
                      <span style={{ color: "#9e9e9e" }}>Generated on save</span>
                    ) : item.id === undefined || item.id === null ? (
                      <span style={{ color: "#9e9e9e" }}>&mdash;</span>
                    ) : (
                      String(item.id).padStart(2, "0")
                    )}
                  </TableCell>
                  <TableCell className={classes.bodyCell}>
                    {canEdit && (
                      <IconButton
                        aria-label="delete"
                        onClick={() => handleDelete(item)}
                        disabled={!!busy || (!item.isNew && item.id === null)}
                      >
                        <DeleteIcon className={classes.deleteIcon} />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "16px",
            marginTop: "16px",
            marginLeft: "16px",
          }}
        >
          <Button
            className={classes.saveButton}
            type="button"
            onClick={handleSaveChanges}
            disabled={!isUpdated || !canEdit || !!busy}
            style={{ opacity: !isUpdated || !canEdit || busy ? 0.7 : 1, color: "#fff" }}
          >
            Save Selection
          </Button>
          <Button
            className={classes.resetChangesButton}
            type="button"
            onClick={handleResetChanges}
            disabled={!isUpdated || !canEdit || !!busy}
            style={{ opacity: !isUpdated || !canEdit || busy ? 0.8 : 1 }}
          >
            Reset Selection
          </Button>
        </div>
      </div>

      {errorMessage && (
        <div style={{ color: "red" }} className="error-message">
          {errorMessage}
        </div>
      )}
      {successMessage && !errorMessage && (
        <div style={{ color: "#2e7d32" }}>{successMessage}</div>
      )}

      {canEdit && (
        <Button variant="outlined" onClick={handleAddRow} className={classes.addButton}>
          <span className={classes.addIconCircle}>+</span>
          Add New Inventory Status
        </Button>
      )}
    </div>
  );
};

export default InventoryStatusTab;
